import anime from 'animejs';
import { live } from '../data/live';

/**
 * The break screen's bottom-left panel, which alternates ad images with incentives.
 * breakCarousel (js/break-carousel.js) decides the order; this only draws one at a time
 * and resolves when it has faded back out.
 */
export const breakIncentivesRenderer = {
    breakIncentivesShowDuration: 15000,

    currentAdBreakIndex: 0,

    /**
     * Shows one ad and resolves when it has faded back out. Resolves `false` when there
     * is no ad at that index, so the driver can move straight on.
     *
     * This used to chain into startBreakIncentiveLoop() and back again, which is how the
     * carousel drove itself — and how, with no ads *and* no incentives, it degenerated
     * into a setTimeout(…, 0) busy loop.
     */
    playAd: (adIndex) => new Promise((resolve) => {
        const adUrl = live.getBreakScreenAds()[adIndex] ?? '';
        if (adUrl === '') {
            resolve(false);
            return;
        }

        const adImage = document.querySelector('.ad-container');
        adImage.style.backgroundImage = `url(${adUrl})`;
        anime({
            targets: '.ad-container',
            duration: 1000,
            easing: 'linear',
            opacity: [
                { value: 1 },
                { value: 0, delay: breakIncentivesRenderer.breakIncentivesShowDuration }
            ],
            complete: () => resolve(true),
        });
    }),

    /** Shows one incentive and resolves when it has faded back out. */
    playBreakIncentive: (incentive) => new Promise((resolve) => {
        breakIncentivesRenderer.populateAndShowBreakIncentive(incentive, resolve);
    }),

    populateAndShowBreakIncentive: (incentive, onDone) => {
        anime({
            targets: '.incentive-break .incentive-container',
            duration: 1000,
            easing: 'linear',
            opacity: [
                { value: 1 },
                { value: 0, delay: breakIncentivesRenderer.breakIncentivesShowDuration }
            ],
            complete: () => onDone?.(),
        });
        breakIncentivesRenderer.populateBreakIncentiveGameData(incentive);
        if (incentive.type === 'fixed')
            breakIncentivesRenderer.populateBreakFixedIncentiveData(incentive);
        else if (incentive.type === 'option')
            breakIncentivesRenderer.populateBreakOptionIncentiveData(incentive);
    },

    populateBreakIncentiveGameData: (incentive) => {
        const titleContainerElement = document.querySelector('.incentive-break .incentive-container .incentive-title');
        const gameNameElement = titleContainerElement.querySelector('.data .game-name');
        const descriptionElement = titleContainerElement.querySelector('.data .description');

        gameNameElement.innerHTML = incentive.gameName;
        descriptionElement.innerHTML = incentive.name;

        const fixedContainerElement = document.querySelector('.incentive-break .incentive-container .incentive-break-data .incentive-fixed');
        const optionContainerElement = document.querySelector('.incentive-break .incentive-container .incentive-break-data .incentive-bars');
        if (incentive.type === 'fixed') {
            fixedContainerElement.style.display = 'flex';
            optionContainerElement.style.display = 'none';
        } else {
            fixedContainerElement.style.display = 'none';
            optionContainerElement.style.display = 'flex';
        }
    },

    populateBreakFixedIncentiveData: (incentive) => {
        const containerElement = document.querySelector('.incentive-break .incentive-container .incentive-break-data .incentive-fixed');
        const innerCircle = containerElement.querySelector('.circle.inner svg path');
        const percentElement = containerElement.querySelector('.totals .percent');
        const totalElement = containerElement.querySelector('.totals .total');

        let formattedTotal = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(incentive.total);
        let goalTotal = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(incentive.goal);

        totalElement.innerHTML = formattedTotal + ' / ' + goalTotal;
        const percent = parseInt(incentive.total * 100 / incentive.goal);
        percentElement.innerHTML = percent + '%';
        innerCircle.style.strokeDasharray = percent + ', 100';
    },

    populateBreakOptionIncentiveData: (incentive) => {
        const containerElement = document.querySelector('.incentive-break .incentive-container .incentive-break-data .incentive-bars');
        const bar1Element = containerElement.querySelector('.bar-1');
        const bar2Element = containerElement.querySelector('.bar-2');
        const bar3Element = containerElement.querySelector('.bar-3');

        const bar1Total = bar1Element.querySelector('.total');
        const bar2Total = bar2Element.querySelector('.total');
        const bar3Total = bar3Element.querySelector('.total');

        const bar1Option = bar1Element.querySelector('.name');
        const bar2Option = bar2Element.querySelector('.name');
        const bar3Option = bar3Element.querySelector('.name');

        const firstTotal = incentive.options[0]?.total ?? 0;
        const secondTotal = incentive.options[1]?.total ?? 0;
        const thirdTotal = incentive.options[2]?.total ?? 0;
        const hasAllZero = firstTotal === 0 && secondTotal === 0 && thirdTotal === 0;

        const hasOption1 = incentive.options[0] !== undefined;
        const hasOption2 = incentive.options[1] !== undefined;
        const hasOption3 = incentive.options[2] !== undefined;

        bar1Element.style.display = 'flex';
        bar2Element.style.display = 'flex';
        bar3Element.style.display = 'flex';

        if (hasOption1) {
            bar1Total.innerHTML = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(firstTotal);
            bar1Option.innerHTML = incentive.options[0].name;
        } else {
            bar1Element.style.display = 'none';
        }
        if (hasOption2) {
            bar2Total.innerHTML = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(secondTotal);
            bar2Option.innerHTML = incentive.options[1].name;
        } else {
            bar2Element.style.display = 'none';
        }
        if (hasOption3) {
            bar3Total.innerHTML = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(thirdTotal);
            bar3Option.innerHTML = incentive.options[2].name;
        } else {
            bar3Element.style.display = 'none';
        }

        if (hasOption1 && hasOption2) {
            if (hasAllZero) {
                bar2Element.style.marginLeft = 0;
                bar3Element.style.marginLeft = 0;
            } else {
                bar2Element.style.marginLeft = - (390 - (390 * (secondTotal - thirdTotal) / (firstTotal - thirdTotal)));
                bar3Element.style.marginLeft = -390;
            }
        }

        const noOptionElement = containerElement.querySelector('.no-options');
        if (!hasOption1) {
            noOptionElement.style.display = 'flex';
        } else {
            noOptionElement.style.display = 'none';
        }
    },

};

window.breakIncentivesRenderer = breakIncentivesRenderer;
